import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import LuggageIcon from '@mui/icons-material/Luggage';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { calculateBookingPrice } from '../../utils/pricingLogic';

const vehicles = [
  {
    id: 1,
    name: 'Mercedes-Maybach V-Class Voyager',
    images: [
      '/images/vehicles/maybach-v-class/interior-2.jpg'
    ],
    capacity: 4,
    luggage: 4,
    features:[
      'Cream Leather Interior',
      '42 Inch TV',
      'Starlight roof',
      'Roof Ambient Lighting',
      'Mini fridge',
      'Climate Control',
      'Entertainment System',
      'USB Charging Ports',
    ],
  },
  {
    id: 2,
    name: 'Mercedes V-Class Tiffany Blue Edition',
    images: [
      '/images/vehicles/tiffany-v-class/exterior-1.jpg',
    ],
    capacity: 5,
    luggage: 4,
    features: [
      'Tiffany Blue Leather Interior',
      '42 Inch TV',
      'Starlight roof',
      'Mini fridge',
      'Climate Control',
      'Ambient Lighting',
      'Entertainment System',
      'USB Charging Ports',
    ]  },
];

// Discount code validation moved to backend for security

const VehicleSelection = ({ data, bookingData, onNext, onBack, variant = 'default' }) => {
  const [selectedVehicle, setSelectedVehicle] = useState(data);
  const [totalPrice, setTotalPrice] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(true);
  const [priceError, setPriceError] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [pricingBreakdown, setPricingBreakdown] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get passenger count from bookingData instead of pickupData
  const passengerCount = Number(bookingData?.pickup?.passengers) || 1;
  
  // Filter vehicles based on capacity
  const availableVehicles = vehicles.filter(vehicle => 
    vehicle.capacity >= passengerCount
  );

  const calculateAndSetPrice = useCallback(async () => {
    if (!bookingData.pickup.locationCoords || !bookingData.pickup.dropoffCoords) {
      return; 
    }
    
    setIsLoadingPrice(true);
    setPriceError(null);

    try {
      // Prepare request data for backend pricing calculation
      const pricingRequest = {
        pickupLocation: {
          name: bookingData.pickup.location,
          coordinates: bookingData.pickup.locationCoords,
        },
        dropoffLocation: {
          name: bookingData.pickup.dropoff,
          coordinates: bookingData.pickup.dropoffCoords,
        },
        durationHours: parseInt(bookingData.pickup.duration, 10),
        pickupTime: bookingData.pickup.time,
        hasReturnJourney: bookingData.pickup.hasReturnJourney,
        discountCode: bookingData.pickup.discountCode || null
      };

      // Add return journey data if applicable
      if (bookingData.pickup.hasReturnJourney && 
          bookingData.pickup.returnLocationCoords && 
          bookingData.pickup.returnDropoffCoords) {
        
        const returnPickupTime = new Date(bookingData.pickup.returnDateTime).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit', 
          hour12: false 
        });

        pricingRequest.returnPickupLocation = {
          name: bookingData.pickup.returnLocation,
          coordinates: bookingData.pickup.returnLocationCoords,
        };
        pricingRequest.returnDropoffLocation = {
          name: bookingData.pickup.returnDropoff,
          coordinates: bookingData.pickup.returnDropoffCoords,
        };
        pricingRequest.returnPickupTime = returnPickupTime;
      }

      // Call backend API for secure pricing calculation
      const response = await fetch('/api/calculate-final-price', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pricingRequest),
      });

      if (!response.ok) {
        throw new Error('Failed to calculate final price');
      }

      const pricingBreakdown = await response.json();
      
      // Set pricing information from backend response
      setTotalPrice(pricingBreakdown.finalPrice);
      setDiscountInfo(pricingBreakdown.discountCodeInfo);
      setPricingBreakdown(pricingBreakdown);
      
      console.log('Pricing breakdown received from backend:', pricingBreakdown);

    } catch (error) {
      console.error('Error calculating price:', error);
      setPriceError('Could not calculate price. Please try again.');
    } finally {
      setIsLoadingPrice(false);
    }
  }, [bookingData]); // Recalculate when booking data changes

  // Use useEffect to trigger the calculation
  useEffect(() => {
    calculateAndSetPrice();
  }, [calculateAndSetPrice]);

  const handleNext = () => {
    if (selectedVehicle && totalPrice !== null && pricingBreakdown) {
      onNext({ 
        vehicle: selectedVehicle, 
        price: totalPrice,
        discountInfo: discountInfo,
        pricing: pricingBreakdown
      });
    }
  };

  const cardStyles = {
    ...(variant === 'home' ? {
      '& .MuiCardMedia-root': {
        height: isMobile ? 120 : 150,
      },
      '& .MuiCardContent-root': {
        p: isMobile ? 1.5 : 2,
      },
      '& .MuiTypography-h6': {
        fontSize: isMobile ? '0.875rem' : '1rem',
        mb: 1,
      },
      '& .MuiChip-root': {
        height: isMobile ? 20 : 24,
        fontSize: isMobile ? '0.675rem' : '0.75rem',
      },
      '& .MuiChip-label': {
        px: isMobile ? 0.5 : 1,
      },
    } : {
      '& .MuiCardMedia-root': {
        height: isMobile ? 150 : 200,
      },
      '& .MuiCardContent-root': {
        p: isMobile ? 2 : 3,
      },
      '& .MuiTypography-h6': {
        fontSize: isMobile ? '1rem' : '1.25rem',
      },
    }),
  };

  const gridSpacing = isMobile ? 1 : (variant === 'home' ? 2 : 3);
  const summaryBoxSpacing = isMobile ? 1 : (variant === 'home' ? 1 : 2);

  return (
    <Card sx={{ bgcolor: variant === 'home' ? 'transparent' : 'background.paper', boxShadow: 'none' }}>
      <CardContent sx={{ p: isMobile ? 1 : 2 }}>
        <Typography variant="h5" gutterBottom sx={{ 
          mb: isMobile ? 1 : (variant === 'home' ? 2 : 4),
          color: variant === 'home' ? 'common.white' : 'inherit',
          fontSize: isMobile ? '1.125rem' : (variant === 'home' ? '1.25rem' : '1.5rem'),
        }}>
          Select Your Vehicle
        </Typography>

        <Grid container spacing={gridSpacing}>
          <Grid item xs={12}>
            {availableVehicles.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                p: 3, 
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.main'
              }}>
                <Typography color="error" variant="h6" gutterBottom>
                  No Vehicles Available
                </Typography>
                <Typography color="text.secondary">
                  We don't have any vehicles that can accommodate {passengerCount} passengers.
                  Please adjust the number of passengers or contact us for special arrangements.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={isMobile ? 1 : 2}>
                {availableVehicles.map((vehicle) => (
                  <Grid item xs={12} sm={6} key={vehicle.id}>
                    <motion.div
                      whileHover={{ scale: isMobile ? 1.01 : 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Card
                        sx={{
                          border: selectedVehicle?.id === vehicle.id ? '2px solid' : 'none',
                          borderColor: 'secondary.main',
                          cursor: 'pointer',
                          ...cardStyles,
                        }}
                        onClick={() => setSelectedVehicle(vehicle)}
                      >
                        <CardMedia
                          component="img"
                          height={isMobile ? (variant === 'home' ? "120" : "150") : (variant === 'home' ? "150" : "200")}
                          image={vehicle.images[0]}
                          alt={vehicle.name}
                        />
                        <CardContent sx={{ p: isMobile ? 1.5 : (variant === 'home' ? 2 : 3) }}>
                          <Typography variant="h6" gutterBottom sx={{
                            fontSize: isMobile ? '0.875rem' : (variant === 'home' ? '1rem' : '1.25rem'),
                            mb: isMobile ? 0.5 : (variant === 'home' ? 1 : 2),
                          }}>
                            {vehicle.name}
                          </Typography>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            <Chip
                              icon={<GroupIcon sx={{ fontSize: isMobile ? '0.875rem' : 'inherit' }} />}
                              label={`${vehicle.capacity} Passengers`}
                              size={isMobile ? "small" : (variant === 'home' ? "small" : "medium")}
                              sx={{ fontSize: isMobile ? '0.675rem' : 'inherit' }}
                            />
                            <Chip
                              icon={<LuggageIcon sx={{ fontSize: isMobile ? '0.875rem' : 'inherit' }} />}
                              label={`${vehicle.luggage} Luggage`}
                              size={isMobile ? "small" : (variant === 'home' ? "small" : "medium")}
                              sx={{ fontSize: isMobile ? '0.675rem' : 'inherit' }}
                            />
                          </Box>
                          {!isMobile && variant !== 'home' && (
                            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                              {vehicle.features.map((feature) => (
                                <Chip
                                  key={feature}
                                  label={feature}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ 
              mt: isMobile ? 1 : 2, 
              display: 'flex', 
              gap: summaryBoxSpacing,
              px: isMobile ? 1 : 0,
            }}>
              <Button
                variant={variant === 'home' ? "outlined" : "contained"}
                onClick={onBack}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  flex: 1,
                  ...(variant === 'home' && {
                    borderColor: 'common.white',
                    color: 'common.white',
                    '&:hover': {
                      borderColor: 'common.white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }),
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!selectedVehicle || isLoadingPrice || priceError}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  flex: 2,
                  ...(variant === 'home' && {
                    bgcolor: 'secondary.main',
                    '&:hover': {
                      bgcolor: 'secondary.dark',
                    },
                  }),
                }}
              >
                {isLoadingPrice 
                  ? 'Calculating Price...' 
                  : priceError 
                  ? 'Error'
                  : `Book Now for £${totalPrice}`}
              </Button>
            </Box>
            {priceError && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {priceError}
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default VehicleSelection; 